import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? <div className="route-link-btn-row">{card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}</div> : null}
      </article>
    ))}
  </div>
);

const heathrowToHenleyRoute = {
  id: "heathrow-to-henley-taxi",
  path: "/heathrow-to-henley-taxi",
  liveStripVariant: "maidenhead",
  seo: {
    title: "Heathrow to Henley Taxi - Fixed-Price Airport Transfers | CheapCabGrab",
    description:
      "Book a fixed-price taxi from Heathrow Airport to Henley-on-Thames, Henley Royal Regatta, riverside hotels and RG9 addresses. Licensed drivers, flight tracking and no surge pricing.",
    keywords:
      "Heathrow to Henley taxi, Heathrow to Henley Royal Regatta taxi, Heathrow to Henley-on-Thames transfer, Heathrow to Henley station taxi, RG9 airport transfer, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/heathrow-to-henley-taxi",
    ogTitle: "Heathrow to Henley Taxi - Fixed Airport Transfers",
    ogDescription:
      "Reliable Heathrow to Henley taxis with fixed fares, licensed drivers, flight tracking and Regatta arrival planning.",
    serviceName: "CheapCabGrab - Heathrow to Henley Taxi",
    areaServed: ["Heathrow Airport", "Henley-on-Thames", "Henley Royal Regatta", "Remenham", "Wargrave", "Marlow"],
    offerCatalogName: "Heathrow to Henley Taxi Routes",
    offerName: "Heathrow Airport to Henley fixed-price taxi",
    offerDescription: "Fixed-price taxi transfers from Heathrow terminals to Henley-on-Thames, Regatta areas, riverside hotels and RG9 addresses.",
    reservationName: "Taxi booking from Heathrow to Henley",
  },
  hero: {
    eyebrow: "Heathrow to Henley taxi route",
    h1: "Heathrow to Henley Taxi - Fixed-Price Airport Transfers",
    subtitle: "Heathrow terminals 2, 3, 4 and 5 to Henley-on-Thames, Henley Royal Regatta, riverside hotels and RG9 addresses.",
    body:
      "Book reliable taxis from Heathrow to Henley with licensed drivers, flight tracking and secure online booking. Fixed upfront fares with no surge pricing.",
  },
  quote: {
    label: "Get instant quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "Heathrow Airport",
    presetDropoff: "Henley-on-Thames",
  },
  flags: [
    "Fixed fares agreed upfront - no meters",
    "Heathrow flight tracking and meet-and-greet support",
    "Henley town, station, hotels and Regatta drop-offs",
    "Saloon, MPV, minibus and coach options",
  ],
  reviewPageKey: "/heathrow-to-henley-taxi",
  reviewAriaLabel: "Heathrow to Henley customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Heathrow to Henley taxis",
  floatingCta: "Book Heathrow to Henley Taxi",
  mobileCta: "Book Heathrow to Henley Taxi",
  supportNudge: "Need Heathrow to Henley help?",
  sections: [
    {
      id: "route-overview",
      title: "Heathrow to Henley at a glance",
      body: "A premium arrival transfer route for Henley Royal Regatta guests, riverside hotel stays, rowing groups, families and Thames Valley visitors.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "Distance: Approx. 24-32 miles depending on terminal and drop-off",
            "Typical time: 45-70 minutes",
            "Typical saloon fare: from GBP75-GBP115",
            "Drop-offs: Henley-on-Thames, Henley station, Remenham, riverside hotels and RG9 addresses",
          ],
        },
        {
          title: "Great for",
          items: [
            "Heathrow arrivals heading to Henley Royal Regatta",
            "Riverside hotel and hospitality drop-offs",
            "Families, rowing crews and visitors with luggage",
            "MPVs and minibuses for Regatta groups",
          ],
        },
        {
          title: "Local planning links",
          links: [
            { to: "/henley-taxi", label: "Henley taxi" },
            { to: "/henley-to-heathrow", label: "Henley to Heathrow", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Heathrow to Henley journey",
      body: "Check your Heathrow terminal, live status and Henley arrival planning before you travel.",
      render: () => cardGrid("route-links-grid", [
        { title: "Heathrow Terminal Finder", body: "Check the correct Heathrow terminal before booking your pickup.", link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" } },
        { title: "Henley taxi hub", body: "For Regatta, town centre, station, hotel and event guidance, use the Henley taxi hub.", link: { to: "/henley-taxi", label: "Open Henley taxi", className: "route-link-btn" } },
        { title: "Group travel", body: "Need more luggage space from Heathrow? Use minibus or coach options for Regatta and airport groups.", links: [{ to: "/minibus", label: "Minibus" }, { to: "/coach", label: "Coach", className: "route-link-btn route-link-btn-secondary" }] },
      ]),
    },
    {
      id: "route-areas",
      title: "Heathrow to Henley drop-off areas",
      body: "Book from Heathrow Airport to Henley-on-Thames, Henley station, Regatta town centre areas, Remenham, Hambleden, Shiplake and nearby RG9 addresses.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Henley and Regatta drop-offs",
          items: [
            "Heathrow to Henley-on-Thames town centre",
            "Heathrow to Henley station",
            "Heathrow to Regatta hotels, restaurants and hospitality",
            "Heathrow to Remenham, Hambleden, Shiplake and nearby addresses",
          ],
          link: { to: "/henley-taxi", label: "Henley taxi hub ->" },
        },
        {
          title: "Nearby Heathrow arrival routes",
          items: [
            "Heathrow to Maidenhead for M4 corridor hotels",
            "Heathrow to Windsor for Castle and visitor groups",
            "Heathrow to Ascot for Royal Ascot and SL5 stays",
          ],
          links: [
            { to: "/heathrow-to-maidenhead", label: "Maidenhead route" },
            { to: "/heathrow-to-windsor", label: "Windsor route", className: "route-link-btn route-link-btn-secondary" },
            { to: "/heathrow-to-ascot-taxi", label: "Ascot route", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Heathrow to Henley taxi - FAQs",
      body: "Route FAQ for Heathrow arrivals travelling to Henley-on-Thames, Henley Royal Regatta and local hotels.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["How long does Heathrow to Henley take?", "Most Heathrow to Henley taxi journeys take around 45-70 minutes, depending on terminal, M4 or M40 routing, Regatta traffic and drop-off point."],
            ["Can you pick up from all Heathrow terminals?", "Yes. Use Heathrow Airport as pickup and add your terminal or flight details during booking so pickup timing can be planned around arrivals."],
            ["Can you drop near Henley Royal Regatta?", "Yes. During Regatta week, drop-off points can be affected by one-way traffic, walking routes and event controls, so allow extra time."],
            ["Do you track flights?", "Yes. Heathrow arrivals are tracked where possible so dispatch timing can account for landing delays and terminal movement."],
          ].map(([title, body]) => <article className="route-faq-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with a Heathrow to Henley booking?",
      body: "For Regatta timing, extra luggage, MPVs, child seats or multi-stop hotel arrivals, use support before booking.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>
            Related pages: <Link to="/henley-taxi">Henley taxi</Link>,{" "}
            <Link to="/henley-to-heathrow">Henley to Heathrow</Link>,{" "}
            <Link to="/heathrow-to-maidenhead">Heathrow to Maidenhead</Link> and{" "}
            <Link to="/heathrow-taxi">Heathrow taxi</Link>.
          </p>
        </div>
      ),
    },
  ],
};

const HeathrowToHenleyTaxi = () => (
  <CcgRoutePremiumPage route={heathrowToHenleyRoute} QuoteComponent={PostcodeQuotenew} />
);

export default HeathrowToHenleyTaxi;
